import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  CircularProgress,
  Alert,
  Box,
  Typography,
  Grid,
  Paper,
  Chip,
  TextField,
  FormControlLabel,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { lessonsAPI } from '../services/api';
import { toast } from 'react-toastify';
import Button from '../components/UIKit/Button';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { formatDate } from '../utils/formatters';

const LessonChangeRequestDetailPage = () => {
  const { t } = useTranslation(['admin', 'common']);
  const { requestId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [changeRequest, setChangeRequest] = useState(null);
  const [originalLesson, setOriginalLesson] = useState(null);
  const [adminComment, setAdminComment] = useState('');
  const [selectedFields, setSelectedFields] = useState([]);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  // Проверка прав администратора
  useEffect(() => {
    if (!user || !user.roles?.includes('admin')) {
      navigate('/');
      toast.error(t('common:errors.accessDenied'));
    }
  }, [user, navigate, t]);

  // Загрузка данных запроса на изменение
  useEffect(() => {
    const fetchChangeRequest = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await lessonsAPI.getChangeRequestById(requestId);
        
        if (response && response.data) {
          const requestData = response.data.data;
          setChangeRequest(requestData);
          setOriginalLesson(requestData.lesson);
          
          // Инициализировать выбранные поля в зависимости от статуса запроса
          if (requestData.status === 'approved' && requestData.approvedFields?.length > 0) {
            // Если запрос одобрен, используем список одобренных полей
            setSelectedFields([...requestData.approvedFields]);
          } else if (requestData.status === 'pending' && requestData.modifiedFields) {
            // Если запрос в ожидании, используем все модифицированные поля
            setSelectedFields([...requestData.modifiedFields]);
          } else if (requestData.modifiedFields) {
            // В других случаях (отклонен), просто инициализируем пустым массивом
            setSelectedFields([]);
          }
        } else {
          setError(t('lessons:changeRequestDetail.errors.notFound'));
        }
      } catch (err) {
        console.error('Ошибка при загрузке запроса на изменение:', err);
        setError(
          err.response?.data?.message || t('lessons:changeRequestDetail.errors.loadError')
        );
      } finally {
        setLoading(false);
      }
    };

    fetchChangeRequest();
  }, [requestId, t]);

  // Обработчик выбора/снятия поля
  const handleFieldToggle = (field) => {
    if (selectedFields.includes(field)) {
      setSelectedFields(selectedFields.filter(f => f !== field));
    } else {
      setSelectedFields([...selectedFields, field]);
    }
  };

  // Обработчики для подтверждения/отклонения запроса
  const handleApproveClick = () => {
    setConfirmAction('approve');
    setConfirmDialogOpen(true);
  };

  const handleRejectClick = () => {
    setConfirmAction('reject');
    setConfirmDialogOpen(true);
  };

  // Обработчик обработки запроса (подтверждение/отклонение)
  const handleProcessRequest = async () => {
    try {
      setSaving(true);
      setConfirmDialogOpen(false);

      const status = confirmAction === 'approve' ? 'approved' : 'rejected';
      
      await lessonsAPI.processChangeRequest(requestId, {
        status,
        adminComment,
        approvedFields: status === 'approved' ? selectedFields : []
      });

      toast.success(
        status === 'approved'
          ? t('admin:changeRequestDetail.messages.approveSuccess')
          : t('admin:changeRequestDetail.messages.rejectSuccess')
      );

      // Перенаправляем на страницу со списком запросов
      navigate('/lesson-change-requests');
    } catch (err) {
      console.error('Ошибка при обработке запроса:', err);
      toast.error(
        err.response?.data?.message || t('admin:changeRequestDetail.errors.processingFailed')
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCancelDialog = () => {
    setConfirmDialogOpen(false);
    setConfirmAction(null);
  };

  // Функция для получения значения поля из объекта урока
  const getFieldValue = (lesson, field) => {
    if (!lesson) return '';
    
    // Для простых полей возвращаем значение напрямую
    if (['title', 'content', 'videoUrl', 'duration', 'type', 'contentType'].includes(field)) {
      return lesson[field] || '';
    }
    
    // Для сложных полей возвращаем JSON строку
    if (['contentBlocks', 'resources', 'practiceExercises'].includes(field)) {
      return JSON.stringify(lesson[field] || [], null, 2);
    }
    
    return '';
  };

      // Функция для отображения измененного поля
    const renderFieldDiff = (field) => {
      // Для старих запитів (без selectedBlocks) показуємо всі поля
      const selectedBlocks = changeRequest?.selectedBlocks || [];
      const isOldRequest = selectedBlocks.length === 0;
      
      // Для старих запитів показуємо всі поля, для нових - тільки вибрані
      const isFieldSelected = isOldRequest || 
                             selectedBlocks.includes(field) || 
                             selectedBlocks.some(blockId => blockId.startsWith('contentBlock_') && field === 'contentBlocks') ||
                             selectedBlocks.includes('main_content') && (field === 'content' || field === 'contentType');
      
      // Якщо поле не вибрано, не відображаємо його
      if (!isFieldSelected) {
        return null;
      }

    const originalValue = getFieldValue(originalLesson, field);
    const newValue = getFieldValue(changeRequest?.proposedChanges, field);
    
    // Определяем, было ли поле одобрено (для обработанных запросов)
    const isApproved = changeRequest?.status === 'approved' && 
                       changeRequest?.approvedFields?.includes(field);
    
    // Определяем, было ли поле отклонено (для обработанных запросов)
    const isRejected = changeRequest?.status === 'approved' && 
                       !changeRequest?.approvedFields?.includes(field);
    
    return (
      <Box sx={{ mt: 2, mb: 2 }} key={field}>
        <FormControlLabel
          control={
            <Checkbox
              checked={selectedFields.includes(field)}
              onChange={() => handleFieldToggle(field)}
              disabled={changeRequest?.status !== 'pending'}
              color={isApproved ? "success" : isRejected ? "error" : "primary"}
            />
          }
          label={
            <Box component="span" sx={{ 
              display: 'flex',
              alignItems: 'center',
              color: isApproved ? "success.main" : isRejected ? "error.main" : "text.primary"
            }}>
              {t(`admin:changeRequestDetail.fields.${field}`)}
              {isApproved && (
                <Chip 
                  size="small" 
                  color="success" 
                  label={t('admin:changeRequestDetail.approved')} 
                  sx={{ ml: 1 }} 
                />
              )}
              {isRejected && (
                <Chip 
                  size="small" 
                  color="error" 
                  label={t('admin:changeRequestDetail.rejected')} 
                  sx={{ ml: 1 }} 
                />
              )}
            </Box>
          }
        />
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>{t('admin:changeRequestDetail.diff.compare')}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('admin:changeRequestDetail.diff.original')}
                </Typography>
                <Paper 
                  variant="outlined" 
                  sx={{ 
                    p: 2, 
                    bgcolor: 'background.paper',
                    maxHeight: '400px',
                    overflow: 'auto',
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'monospace'
                  }}
                >
                  {originalValue}
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('admin:changeRequestDetail.diff.proposed')}
                </Typography>
                <Paper 
                  variant="outlined" 
                  sx={{ 
                    p: 2, 
                    bgcolor: 'background.paper',
                    maxHeight: '400px',
                    overflow: 'auto',
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'monospace',
                    border: isApproved ? '2px solid #4caf50' : isRejected ? '2px solid #f44336' : undefined
                  }}
                >
                  {newValue}
                </Paper>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Box>
    );
  };

  const handleBackClick = () => {
    navigate('/lesson-change-requests');
  };

  if (loading) {
    return (
      <Container sx={{ mt: 4 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '300px',
          }}
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
          {error}
        </Alert>
        <Button 
          variant="outlined" 
          startIcon={<ArrowBackIcon />} 
          onClick={handleBackClick}
        >
          {t('admin:changeRequestDetail.buttons.back')}
        </Button>
      </Container>
    );
  }

  if (!changeRequest) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
          {t('admin:changeRequestDetail.requestNotFound')}
        </Alert>
        <Button 
          variant="outlined" 
          startIcon={<ArrowBackIcon />} 
          onClick={handleBackClick}
        >
          {t('admin:changeRequestDetail.buttons.back')}
        </Button>
      </Container>
    );
  }

  const isStatusPending = changeRequest.status === 'pending';

  return (
    <Container sx={{ mt: 4, mb: 4 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4,
        }}
      >
        <Typography variant="h4" component="h1">
          {t('admin:changeRequestDetail.title')}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBackClick}
        >
          {t('admin:changeRequestDetail.buttons.back')}
        </Button>
      </Box>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {t('admin:changeRequestDetail.lesson')}
            </Typography>
            <Typography variant="body1" gutterBottom>
              {originalLesson?.title}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {t('admin:changeRequestDetail.status')}
            </Typography>
            <Chip
              label={t(`admin:lessonChangeRequests.status.${changeRequest.status}`)}
              color={
                changeRequest.status === 'pending'
                  ? 'warning'
                  : changeRequest.status === 'approved'
                  ? 'success'
                  : 'error'
              }
              icon={
                changeRequest.status === 'pending'
                  ? null
                  : changeRequest.status === 'approved'
                  ? <CheckCircleIcon />
                  : <CancelIcon />
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {t('admin:changeRequestDetail.requestedBy')}
            </Typography>
            <Typography variant="body1" gutterBottom>
              {changeRequest.requestedBy?.userMeta?.fullName || changeRequest.requestedBy?.userMeta?.email || t('common:unknownUser')}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {t('admin:changeRequestDetail.createdAt')}
            </Typography>
            <Typography variant="body1" gutterBottom>
              {formatDate(changeRequest.createdAt)}
            </Typography>
          </Grid>
          {changeRequest.processedBy && (
            <>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('admin:changeRequestDetail.processedBy')}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {changeRequest.processedBy?.username || t('common:unknownUser')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('admin:changeRequestDetail.processedAt')}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {formatDate(changeRequest.processedAt)}
                </Typography>
              </Grid>
            </>
          )}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {t('admin:changeRequestDetail.userComment')}
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.paper' }}>
              <Typography variant="body1">
                {changeRequest.comment || t('admin:changeRequestDetail.noComment')}
              </Typography>
            </Paper>
          </Grid>
          {changeRequest.adminComment && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {t('admin:changeRequestDetail.adminComment')}
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.paper' }}>
                <Typography variant="body1">
                  {changeRequest.adminComment}
                </Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      </Paper>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          {t('admin:changeRequestDetail.changesTitle')}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {isStatusPending 
            ? t('admin:changeRequestDetail.selectFieldsMessage')
            : t('admin:changeRequestDetail.changesOverview')}
        </Typography>

        {/* Список измененных полей */}
        <Paper sx={{ p: 3, mt: 2 }}>
          {changeRequest.modifiedFields && changeRequest.modifiedFields.length > 0 ? (
            changeRequest.modifiedFields.map((field) => renderFieldDiff(field))
          ) : (
            <Typography variant="body1">
              {t('admin:changeRequestDetail.noChangesFound')}
            </Typography>
          )}
        </Paper>
      </Box>

      {/* Removed New Changes Section */}

      {isStatusPending && (
        <>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom>
              {t('admin:changeRequestDetail.adminResponseTitle')}
            </Typography>
            <TextField
              label={t('admin:changeRequestDetail.adminCommentLabel')}
              multiline
              rows={4}
              value={adminComment}
              onChange={(e) => setAdminComment(e.target.value)}
              fullWidth
              helperText={t('admin:changeRequestDetail.adminCommentHelperText')}
            />
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4, gap: 2 }}>
            <Button
              variant="outlined"
              color="error"
              startIcon={<CancelIcon />}
              onClick={handleRejectClick}
              disabled={saving}
            >
              {saving && confirmAction === 'reject'
                ? t('admin:changeRequestDetail.buttons.processing')
                : t('admin:changeRequestDetail.buttons.reject')}
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircleIcon />}
              onClick={handleApproveClick}
              disabled={saving || selectedFields.length === 0}
            >
              {saving && confirmAction === 'approve'
                ? t('admin:changeRequestDetail.buttons.processing')
                : t('admin:changeRequestDetail.buttons.approve')}
            </Button>
          </Box>
        </>
      )}

      {/* Диалог подтверждения */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelDialog}
      >
        <DialogTitle>
          {confirmAction === 'approve'
            ? t('admin:changeRequestDetail.confirmApproveTitle')
            : t('admin:changeRequestDetail.confirmRejectTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmAction === 'approve'
              ? selectedFields.length > 0
                ? t('admin:changeRequestDetail.confirmApproveMessage', { count: selectedFields.length })
                : t('admin:changeRequestDetail.confirmApproveNoFieldsMessage')
              : t('admin:changeRequestDetail.confirmRejectMessage')}
          </DialogContentText>
          {confirmAction === 'approve' && selectedFields.length > 0 && (
            <List dense>
              {selectedFields.map((field) => (
                <ListItem key={field}>
                  <ListItemText primary={t(`admin:changeRequestDetail.fields.${field}`)} />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDialog} color="inherit">
            {t('common:actions.cancel')}
          </Button>
          <Button
            onClick={handleProcessRequest}
            color={confirmAction === 'approve' ? 'success' : 'error'}
            variant="contained"
            disabled={confirmAction === 'approve' && selectedFields.length === 0}
          >
            {confirmAction === 'approve'
              ? t('admin:changeRequestDetail.buttons.confirmApprove')
              : t('admin:changeRequestDetail.buttons.confirmReject')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default LessonChangeRequestDetailPage; 